// Note editor: a plain textarea (like notd), bound to the note's Yjs document.
// Typing → diff → splice; changes from other devices → textarea updated with the cursor anchored
// (relative positions survive concurrent inserts before it).

import { getSpace } from '../space/current';
import { randomHex } from '../core/ids';
import { diff } from '../notes/diff';
import { COLORS } from '../notes/notd';
import type { Space } from '../space/space';
import type { NoteDoc } from '../notes/doc';
import { h, keep } from './dom';
import type { View } from './view';

const EDITOR = Symbol('editor');
const COLOR_LABEL: Record<string, string> = { red: 'rouge', amber: 'ambre', green: 'vert', blue: 'bleu', purple: 'violet', gray: 'gris' };

export function editorView(root: HTMLElement, id: string): View {
  let space: Space | null = null;
  let doc: NoteDoc | null = null;
  const cleanups: (() => void)[] = [];
  let prev = '';

  const textarea = h('textarea', { class: 'editor', placeholder: 'Écris en Markdown…', spellcheck: true, 'aria-label': 'Contenu de la note' });
  const title = h('input', { class: 'field title-field', placeholder: 'Titre', 'aria-label': 'Titre' });
  const toolbar = h('div', { class: 'row toolbar' });
  let anchors: ReturnType<NoteDoc['anchor']>[] = [];

  const remember = () => {
    if (doc) anchors = [doc.anchor(textarea.selectionStart), doc.anchor(textarea.selectionEnd)];
  };

  function renderToolbar() {
    if (!doc || !space) return;
    const f = doc.fields();
    const folders = space.notes.activeFolders();
    const color = h(
      'select',
      { class: 'field small-field', 'aria-label': 'Couleur', onchange: (e: Event) => doc!.set({ color: (e.target as HTMLSelectElement).value || null }) },
      h('option', { value: '', text: 'couleur' }),
      ...COLORS.map((c) => h('option', { value: c, text: COLOR_LABEL[c], selected: f.color === c })),
    );
    const folder = h(
      'select',
      {
        class: 'field small-field',
        'aria-label': 'Dossier',
        onchange: async (e: Event) => {
          const value = (e.target as HTMLSelectElement).value;
          if (value !== '__new') return doc!.set({ folder: value || null });
          const name = prompt('Nom du dossier')?.trim();
          if (!name) return renderToolbar();
          const fid = randomHex(8);
          await space!.notes.setFolder({ id: fid, name: name.slice(0, 60), color: null, position: folders.length });
          doc!.set({ folder: fid });
        },
      },
      h('option', { value: '', text: 'sans dossier' }),
      ...folders.map((d) => h('option', { value: d.id, text: d.name, selected: f.folder === d.id })),
      h('option', { value: '__new', text: '+ nouveau dossier…' }),
    );
    // Due dates are stored like notd: midnight UTC of the chosen day.
    const due = h('input', {
      type: 'date',
      class: 'field small-field',
      'aria-label': 'Échéance',
      value: f.due != null ? new Date(f.due).toISOString().slice(0, 10) : '',
      onchange: (e: Event) => {
        const v = (e.target as HTMLInputElement).value;
        doc!.set({ due: v ? Date.parse(`${v}T00:00:00Z`) : null });
      },
    });
    toolbar.replaceChildren(
      ...keep(
        h('button', { class: `btn small${f.pinned ? ' primary' : ''}`, text: f.pinned ? 'Épinglée' : 'Épingler', onclick: () => doc!.set({ pinned: !f.pinned }) }),
        due,
        color,
        folder,
        h('button', { class: 'btn small', text: f.archived ? 'Désarchiver' : 'Archiver', onclick: () => doc!.set({ archived: !f.archived }) }),
        h('a', { class: 'btn small', href: `#/notes/${id}/history`, text: 'Historique' }),
      ),
    );
  }

  async function mount() {
    space = await getSpace();
    if (!space) return void (location.hash = '#/notes');
    doc = await space.notes.open(id);
    const d = doc;
    textarea.value = prev = d.text();
    title.value = d.fields().title;
    if (!space.journal.isMember) textarea.readOnly = title.readOnly = true;

    textarea.addEventListener('input', () => {
      const s = diff(prev, textarea.value, textarea.selectionEnd);
      if (s) d.splice(s.at, s.del, s.insert, EDITOR);
      prev = textarea.value;
      remember();
    });
    for (const type of ['select', 'keyup', 'pointerup', 'focus']) textarea.addEventListener(type, remember);
    title.addEventListener('input', () => d.set({ title: title.value }, EDITOR));

    const onBody = (_e: unknown, tr: { origin: unknown }) => {
      if (tr.origin === EDITOR) return;
      const focused = document.activeElement === textarea;
      const [a, b] = anchors.map((p) => d.resolve(p));
      textarea.value = prev = d.text();
      if (focused && a != null) textarea.setSelectionRange(a, b ?? a);
    };
    d.body.observe(onBody);
    cleanups.push(() => d.body.unobserve(onBody));
    const onMeta = (_e: unknown, tr: { origin: unknown }) => {
      if (tr.origin !== EDITOR && document.activeElement !== title) title.value = d.fields().title;
      renderToolbar();
    };
    d.meta.observe(onMeta);
    cleanups.push(() => d.meta.unobserve(onMeta));
    const onFolders = () => renderToolbar();
    space.notes.addEventListener('folders', onFolders);
    cleanups.push(() => space!.notes.removeEventListener('folders', onFolders));

    renderToolbar();
    root.replaceChildren(h('div', { class: 'row' }, h('a', { class: 'btn small', href: '#/notes', text: '← Notes' }), title), toolbar, textarea);
    if (!prev && !title.value) textarea.focus();
  }

  const onHidden = () => document.visibilityState === 'hidden' && void space?.notes.flush();
  document.addEventListener('visibilitychange', onHidden);
  addEventListener('pagehide', onHidden);
  root.replaceChildren(h('p', { class: 'dim', text: 'Ouverture…' }));
  void mount();

  return {
    destroy() {
      document.removeEventListener('visibilitychange', onHidden);
      removeEventListener('pagehide', onHidden);
      for (const c of cleanups) c();
      // A note left empty is archived rather than kept in the list (non-destructive, like notd).
      if (doc && !doc.text().trim() && !doc.fields().title.trim() && !doc.fields().archived) doc.set({ archived: true });
      void space?.notes.flush();
    },
  };
}
